import React, { Fragment, useState, useCallback, useEffect } from 'react';
import { useTranslate } from 'react-admin';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { DatePicker } from '@material-ui/pickers';
import { time } from '~/utils';

import { filterChange, selectors, cleanupFilters } from '~/ducks/virtualizedList';
import { Account, Moment } from '~/types';
import { debounce } from 'lodash';
import BackgroundTaskExport from '~/components/BackgroundTaskExport';

interface Props {
  resource: string;
  record: Account;
}

const label = (key: string): string => `resources.accounts.show.balances.${key}`;

const BalancesFilters = (props: Props) => {
  const { resource, record } = props;

  const filters = useSelector(selectors.getFilters<{ [x: string]: any }>(resource));
  const [localValues, setLocalValues] = useState(filters);
  useEffect(() => {
    setLocalValues({ ...filters });
  }, [filters]);

  const classes = useStyles();
  const dispatch = useDispatch();
  const translate = useTranslate();
  const t = (key: string): string => translate(label(key));

  const dispatchFilterChange = (name: string, value: string | Moment) => {
    dispatch(filterChange({ resource, filters: { [name]: value || null } }));
  };
  const debouncedDispatchFilterChange = useCallback(debounce(dispatchFilterChange, 1000), []);

  const handleChange = (name: string) => (eventOrData: any) => {
    switch (name) {
      case 'bankingDayFrom': {
        setLocalValues((prev) => ({
          ...prev,
          [name]: (eventOrData as Moment).startOf('day'),
        }));
        dispatchFilterChange(name, eventOrData);
        break;
      }
      case 'bankingDayTo': {
        setLocalValues((prev) => ({
          ...prev,
          [name]: (eventOrData as Moment).endOf('day'),
        }));
        dispatchFilterChange(name, eventOrData);
        break;
      }
      default: {
        const value = eventOrData?.target?.value || '';
        setLocalValues((prev) => ({
          ...prev,
          [name]: value as string,
        }));
        debouncedDispatchFilterChange(name, value as string);
      }
    }
  };

  useEffect(() => {
    dispatch(
      filterChange({
        resource,
        filters: {
          bankingDayFrom: time().subtract(1, 'months').startOf('day'),
          bankingDayTo: time().endOf('day'),
          accountId: record.id,
        },
      })
    );
    return () => {
      dispatch(cleanupFilters({ resource }));
    };
  }, [dispatch, record.id, resource]);

  return (
    <Fragment>
      {filters && (
        <div className={classes.container}>
          <div className={classes.inputsGroup}>
            <DatePicker
              className={classes.datePickers}
              format='YYYY-MM-DD'
              margin='normal'
              label={t('bankingDayFrom')}
              value={localValues.bankingDayFrom}
              onChange={handleChange('bankingDayFrom')}
              minDate={time(MIN_DATE)}
              maxDate={time()}
            />
            <DatePicker
              className={classes.datePickers}
              format='YYYY-MM-DD'
              margin='normal'
              label={t('bankingDayTo')}
              value={localValues.bankingDayTo}
              onChange={handleChange('bankingDayTo')}
              minDate={time(MIN_DATE)}
              maxDate={time()}
            />
          </div>
          <BackgroundTaskExport type='REPORT_BALANCES_EXPORT' queryArguments={filters} />
        </div>
      )}
    </Fragment>
  );
};

const MIN_DATE = '2025-07-01';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-end',
    paddingBottom: theme.spacing(1),
  },
  inputsGroup: {
    display: 'flex',
    width: '100%',
    flexWrap: 'wrap',
  },
  formControl: {
    paddingBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
    width: 200,
    '&:last-child': {
      marginRight: 0,
    },
  },
  datePickers: {
    paddingBottom: theme.spacing(1),
    marginRight: theme.spacing(1),
    marginTop: 0,
    marginBottom: 0,
    width: 200,
    '& > div': {
      paddingRight: 0,
      '& > div': {
        marginLeft: 0,
      },
    },
  },
}));

export default BalancesFilters;
